// Pattern: the 16x4 step grid. Same interface *type* as the pads (both are
// direct manipulation, both count as "pads" in the study), but this one is a
// NexusUI Sequencer -- nothing here is a measured variable, so there is no
// reason to hand-roll a matrix widget.

import './pattern.css'
import Nexus from 'nexusui'
import { SOUNDS } from '../engine/presets.js'

export const meta = {
  id: 'pattern',
  label: 'Pattern',
  order: 20,
  paramGroup: 'pads',
  blurb: 'the 16-step grid; tap cells to build a loop',
  studyCondition: false,
}

const ROWS = SOUNDS.length
const COLS = 16

// Cell height is clamped so the grid stays tappable on a phone and does not
// become a letterbox on a desktop.
const MIN_ROW_PX = 20
const MAX_ROW_PX = 44

const cssVar = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()

export function mount({ api, root, params }) {
  root.innerHTML = `
    <div class="pattern">
      <div class="pattern__body">
        <div class="pattern__labels"></div>
        <div class="pattern__grid">
          <div class="pattern__target"></div>
          <div class="pattern__playhead" hidden></div>
        </div>
      </div>
      <div class="pattern__bar">
        <label class="pattern__tempo">
          <span>tempo</span>
          <input type="range" min="40" max="240" step="1">
          <output></output>
        </label>
        <button type="button" class="pattern__clear">clear</button>
      </div>
    </div>`

  const labels = root.querySelector('.pattern__labels')
  const gridEl = root.querySelector('.pattern__grid')
  const target = root.querySelector('.pattern__target')
  const playhead = root.querySelector('.pattern__playhead')
  const tempoInput = root.querySelector('.pattern__tempo input')
  const tempoOut = root.querySelector('.pattern__tempo output')
  const clearBtn = root.querySelector('.pattern__clear')

  for (const sound of SOUNDS) {
    const el = document.createElement('span')
    el.className = 'pattern__label'
    el.textContent = sound
    el.style.setProperty('--row-color', `var(--${sound})`)
    labels.appendChild(el)
  }

  const seq = new Nexus.Sequencer(target, {
    rows: ROWS,
    columns: COLS,
    mode: 'toggle',
    size: [Math.max(gridEl.clientWidth || 320, 160), ROWS * MIN_ROW_PX],
  })
  seq.colorize('fill', cssVar('--bg-sunken') || '#0c0d12')
  seq.colorize('accent', cssVar('--text-dim') || '#9a9db0')

  // ROW INDEXING, checked against nexusui 2.1.6 source rather than the docs:
  // the Sequencer's `change` event carries `row` from MatrixModel.locate(),
  // which is `floor(index / columns)`, and buildInterface lays cell `i` out
  // at `top = row * cellHeight`. So row 0 is the TOP row and no flip is
  // needed -- row 0 maps straight onto SOUNDS[0] ('kick').
  // The "bottom row first" warning in the Nexus docs applies only to the
  // `step` event's array payload (it calls .reverse()), which we do not use:
  // the playhead comes from the engine, not from Nexus's own interval.

  const cellAt = (row, column) => seq.cells[seq.matrix.indexOf(row, column)]

  // Nexus has one accent colour for the whole widget, so per-track colour has
  // to be painted on: an inline `fill` style beats the SVG presentation
  // attribute Nexus writes, and survives its re-renders.
  function paint(row, column) {
    const cell = cellAt(row, column)
    cell.pad.style.fill = cell.state ? `var(--${SOUNDS[row]})` : ''
  }

  // Programmatic writes go through turnOn/turnOff(false) so they do not
  // re-enter `change` and bounce back to the engine.
  function setCellQuietly(row, column, on) {
    const cell = cellAt(row, column)
    seq.matrix.pattern[row][column] = on
    if (cell.state !== on) {
      if (on) cell.turnOn(false)
      else cell.turnOff(false)
    }
    paint(row, column)
  }

  function syncFromEngine() {
    const pattern = api.getPattern()
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) setCellQuietly(r, c, !!pattern[r]?.[c])
    }
  }

  let selfEdit = false

  seq.on('change', ({ row, column, state }) => {
    api.unlock()
    selfEdit = true
    api.setStep(row, column, !!state)
    selfEdit = false
    paint(row, column)
  })

  function showTempo(bpm) {
    tempoInput.value = String(Math.round(bpm))
    tempoOut.textContent = `${Math.round(bpm)} bpm`
  }

  const onTempo = () => {
    showTempo(Number(tempoInput.value))
    api.setTempo(Number(tempoInput.value))
  }
  const onClear = () => {
    selfEdit = true
    api.clear()
    selfEdit = false
    syncFromEngine()
  }

  tempoInput.addEventListener('input', onTempo)
  clearBtn.addEventListener('click', onClear)

  showTempo(api.tempo)
  syncFromEngine()

  const offStep = api.onStep((step) => {
    playhead.hidden = false
    playhead.style.setProperty('--step', String(step))
  })

  const offChange = api.onChange((evt) => {
    // The CLI (and the week-12 agent) edit the same pattern we do.
    if (evt.type === 'pattern' && !selfEdit) syncFromEngine()
    if (evt.type === 'tempo') showTempo(evt.value)
    if (evt.type === 'transport' && !evt.playing) playhead.hidden = true
  })

  // The pads params are what this mode is filed under, and only `labels`
  // means anything here.
  const applyParams = (p) => labels.classList.toggle('pattern__labels--hidden', !p.labels)
  applyParams(params.group('pads'))
  const offParams = params.onGroupChange('pads', applyParams)

  // Nexus widgets are sized in pixels, so responsiveness is ours to do.
  let lastWidth = 0
  function fit() {
    const w = Math.floor(gridEl.clientWidth)
    if (w < 40 || w === lastWidth) return
    lastWidth = w
    const rowPx = Math.min(MAX_ROW_PX, Math.max(MIN_ROW_PX, w / COLS))
    const h = Math.round(rowPx * ROWS)
    seq.resize(w, h)
    root.querySelector('.pattern').style.setProperty('--grid-h', `${h}px`)
  }

  const ro = new ResizeObserver(fit)
  ro.observe(gridEl)
  fit()

  return {
    unmount() {
      ro.disconnect()
      offStep()
      offChange()
      offParams()
      tempoInput.removeEventListener('input', onTempo)
      clearBtn.removeEventListener('click', onClear)
      seq.destroy()
      root.innerHTML = ''
    },
  }
}
